/**
 * Privacy-safe analytics bridge (see GTM tracking spec).
 *
 * Embedded (iframe on finavest.ch): every event is posted to the parent as
 * `{ type: 'finavest:analytics', event, params }`; the embed snippet
 * (public/embed.js) verifies origin + `e.source` and pushes the event into the
 * parent page's `window.dataLayer`, enriched with `embed_instance`,
 * `trigger_source`, `page_path` and `page_title`. GTM cannot look inside the
 * iframe — this relay is the standard pattern.
 *
 * Standalone (direct visit on tools.): pushed straight into the local
 * `window.dataLayer` with `embed_instance: 'standalone'`.
 *
 * HARD RULES (spec):
 *  - never PII — no names, e-mail, phone, address, lead ids;
 *  - never raw amounts — numeric values leave the app ONLY as bucket strings,
 *    bucketed HERE in the app (boundaries below are spec-exact).
 * The payload is PII-free by construction, so posting to `'*'` is safe; the
 * parent snippet filters by origin + source before relaying.
 */

export type AnalyticsEvent =
  | 'rechner_loaded'
  | 'rechner_step_view'
  | 'rechner_field_change'
  | 'rechner_calculate_click'
  | 'rechner_verdict_shown'
  | 'rechner_gate_view'
  | 'rechner_gate_submit'
  | 'rechner_gate_success'
  | 'rechner_gate_error'
  | 'rechner_gate_field'
  | 'rechner_beratung_click'
  | 'rechner_beratung_submit'
  | 'rechner_beratung_success'
  | 'rechner_beratung_error'
  | 'rechner_beratung_field'
  // --- Zinsvergleich (W6b) — same hard rules: buckets/enums only, never PII ---
  | 'vergleich_view'
  | 'vergleich_product_change'
  | 'vergleich_volume_change'
  | 'vergleich_beratung_click'
  | 'vergleich_beratung_submit'
  | 'vergleich_beratung_success'
  | 'vergleich_beratung_error'
  | 'vergleich_beratung_field'
  | 'vergleich_to_rechner';

/** Only enum-ish strings ever leave the app: buckets, verdicts, machine codes. */
export type AnalyticsParams = Record<string, string>;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const MESSAGE_TYPE = 'finavest:analytics';

/** Emit one analytics event. Must never throw — analytics may not break the Rechner. */
export function track(event: AnalyticsEvent, params: AnalyticsParams = {}): void {
  try {
    if (window === window.top) {
      // Standalone on tools. — push locally (for a GTM container on tools. later).
      const dl = (window.dataLayer = window.dataLayer ?? []);
      dl.push({ ...params, event, embed_instance: 'standalone' });
    } else {
      // Embedded — relay via the parent; embed.js adds the instance/page context.
      window.parent.postMessage({ type: MESSAGE_TYPE, event, params }, '*');
    }
  } catch {
    /* ignore */
  }
}

/**
 * Fire-only per-field "engagement" tracking for the lead/Beratung forms: emits
 * `event` with `{ field }` the FIRST time a field is touched (input/change),
 * once per field per page-load. PII-safe by construction — only the field NAME
 * leaves the app, never the entered value (name/e-mail/phone stay in the form).
 * Lets GTM see field-by-field drop-off via one event + a `field` variable.
 * Null elements are skipped so a markup change can never break the funnel.
 */
export function bindFieldTouch(
  event: AnalyticsEvent,
  fields: { field: string; el: HTMLElement | null }[],
): void {
  const seen = new Set<string>();
  for (const { field, el } of fields) {
    if (!el) continue;
    const fire = (): void => {
      if (seen.has(field)) return;
      seen.add(field);
      track(event, { field });
    };
    el.addEventListener('input', fire); // text/email: first keystroke
    el.addEventListener('change', fire); // selects/checkbox (and committed text)
  }
}

// ---------- buckets (boundaries exactly as in the GTM tracking spec) ----------

/** Float guard so an exact boundary value (e.g. LTV 80.0%) never flips a bucket. */
const EPS = 1e-9;

/** Kaufpreis / Eigenmittel (CHF). */
export function amountBucket(chf: number): string {
  if (chf < 500_000) return '<500k';
  if (chf < 750_000) return '500–750k';
  if (chf < 1_000_000) return '750k–1m';
  if (chf < 1_500_000) return '1–1.5m';
  if (chf < 2_000_000) return '1.5–2m';
  return '>2m';
}

/** Bruttojahreseinkommen (CHF). */
export function incomeBucket(chf: number): string {
  if (chf < 80_000) return '<80k';
  if (chf < 120_000) return '80–120k';
  if (chf < 160_000) return '120–160k';
  if (chf < 250_000) return '160–250k';
  return '>250k';
}

/** Belehnung, as a ratio (0.8 = 80%). */
export function ltvBucket(ltv: number): string {
  const p = ltv * 100;
  if (p <= 65 + EPS) return '<=65';
  if (p <= 80 + EPS) return '65–80';
  if (p <= 90 + EPS) return '80–90';
  return '>90';
}

/** Tragbarkeit / Wohnkostenquote, as a ratio (0.33 = 33%). */
export function tragbarkeitBucket(ratio: number): string {
  const p = ratio * 100;
  if (p <= 30 + EPS) return '<=30';
  if (p <= 33 + EPS) return '30–33';
  return '>33';
}
